import axios from 'axios'
import type { EventClient, AccountContext } from '~/lib/types/EventClient'
import type {
  EventData,
  EventClientConfig,
  EventEndpoint,
  EventPayloadMap,
  CampaignSubmitPayload,
  CampaignEntryAcceptPayload,
  CampaignCreatePayload,
} from '~/lib/types/Event'
import { EVENT_ENDPOINTS } from '~/lib/types/Event'
import { EventBuffer } from './EventBuffer'
import { RetryManager } from './RetryManager'
import { EventStorage } from './EventStorage'

export class HttpEventClient implements EventClient {
  private buffer: EventBuffer
  private retryManager: RetryManager
  private storage: EventStorage
  private isDestroyed = false
  private accountContext: AccountContext | null = null

  constructor(private readonly config: EventClientConfig) {
    this.retryManager = new RetryManager({
      maxRetries: config.maxRetries,
      backoffBaseMs: config.backoffBaseMs,
    })

    this.storage = new EventStorage(config.storageKey)

    this.buffer = new EventBuffer(config.bufferSize, config.flushIntervalMs, (events) => this.sendEvents(events))

    this.retryFailedEvents()
  }

  async sendCampaignSubmit(payload: CampaignSubmitPayload): Promise<void> {
    await this.sendEvent(EVENT_ENDPOINTS.CAMPAIGN_SUBMIT, payload)
  }

  async sendCampaignEntryAccept(payload: CampaignEntryAcceptPayload): Promise<void> {
    await this.sendEvent(EVENT_ENDPOINTS.CAMPAIGN_ENTRY_ACCEPT, payload)
  }

  async sendCampaignCreate(payload: CampaignCreatePayload): Promise<void> {
    await this.sendEvent(EVENT_ENDPOINTS.CAMPAIGN_CREATE, payload)
  }

  setAccountContext(context: AccountContext): void {
    this.accountContext = context
  }

  clearAccountContext(): void {
    this.accountContext = null
  }

  async flush(): Promise<void> {
    if (this.isDestroyed) return
    await this.buffer.flush()
  }

  async destroy(): Promise<void> {
    this.isDestroyed = true
    await this.buffer.flush()
    this.buffer.clear()
  }

  private async sendEvent<T extends EventEndpoint>(endpoint: T, payload: EventPayloadMap[T]): Promise<void> {
    if (this.isDestroyed) {
      throw new Error('EventClient has been destroyed')
    }

    // イベント記録時のタイムスタンプとコンテキスト情報を自動付与
    const timestamp = Date.now()
    const enrichedPayload = {
      ...payload,
      custom: {
        ...payload.custom,
        timestamp,
        ...(this.accountContext && {
          adv_account_id: this.accountContext.adv_account_id,
          user_adv_id: this.accountContext.user_adv_id,
        }),
      },
    } as EventPayloadMap[T]

    const event: EventData<T> = {
      endpoint,
      payload: enrichedPayload,
      timestamp,
      id: this.generateEventId(),
    }

    this.buffer.add(event as EventData)
  }

  private async sendEvents(events: EventData[]): Promise<void> {
    const pendingEvents: EventData[] = []
    const successfulEventIds: string[] = []

    const chunks = this.chunkArray(events, this.config.concurrency)

    for (const chunk of chunks) {
      const results = await Promise.allSettled(chunk.map((event) => this.processEvent(event)))

      for (const [index, result] of results.entries()) {
        const event = chunk[index]
        if (!event) continue

        if (result.status === 'fulfilled' && result.value.success) {
          successfulEventIds.push(event.id)
        } else {
          console.warn(
            `Failed to send event ${event.id}:`,
            result.status === 'rejected' ? result.reason : result.value.error,
          )

          // RetryManagerで既に最大回数リトライ済みのため、失敗したイベントは永続化
          pendingEvents.push(event)
        }
      }
    }

    if (pendingEvents.length > 0) {
      this.storage.saveFailedEvents(pendingEvents)
    }

    if (successfulEventIds.length > 0) {
      this.storage.removeFailedEvents(successfulEventIds)
    }
  }

  private async processEvent(event: EventData): Promise<{ success: boolean; error?: Error }> {
    try {
      await this.retryManager.executeWithRetry(() => this.sendEventHttp(event))
      return { success: true }
    } catch (error) {
      return { success: false, error: error as Error }
    }
  }

  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = []
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize))
    }
    return chunks
  }

  private async sendEventHttp(event: EventData): Promise<void> {
    const url = `${this.config.baseUrl}${event.endpoint}`

    await axios.post(url, event.payload, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }

  private async retryFailedEvents(): Promise<void> {
    if (this.isDestroyed) return

    const failedEvents = this.storage.getFailedEvents()
    if (failedEvents.length === 0) return

    try {
      await this.sendEvents(failedEvents)
    } catch (error) {
      console.warn('Failed to retry failed events:', error)
    }
  }

  private generateEventId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }
}
